import { Component } from "react";
import { useLocation } from "react-router-dom";

class ErrorBoundaryInner extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log completo in console per diagnosi (visibile anche in produzione/Azure)
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Errore di render catturato:", error, info?.componentStack);
    this.setState({ componentStack: info?.componentStack || "" });
  }

  componentDidUpdate(prevProps) {
    // Reset automatico quando cambia la rotta
    if (this.state.hasError && prevProps.routeKey !== this.props.routeKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    const err = this.state.error;
    const msg = (err && (err.message || String(err))) || "Errore sconosciuto";
    const stack = (err && err.stack) || "";
    const compStack = this.state.componentStack || "";
    const full = [msg, stack && `\n\n— Stack —\n${stack}`, compStack && `\n\n— Componenti —\n${compStack}`].filter(Boolean).join("");
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="error-boundary">
        <div className="max-w-lg w-full rounded-sm border border-border bg-white shadow-sm p-8 text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center text-red-600 text-2xl font-bold">!</div>
          <h1 className="font-heading text-xl font-bold text-foreground mb-2">Si è verificato un errore</h1>
          <p className="text-sm text-muted-foreground mb-5">
            La pagina non può essere visualizzata a causa di un errore imprevisto. Riprova ricaricando la pagina; se il problema persiste, comunica il messaggio qui sotto all'assistenza.
          </p>
          <div className="flex items-center justify-center gap-3 mb-5">
            <button
              onClick={() => window.location.reload()}
              className="rounded-sm bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
              data-testid="error-reload-btn"
            >
              Ricarica la pagina
            </button>
            <button
              onClick={() => { window.location.href = "/"; }}
              className="rounded-sm border border-border px-4 py-2 text-sm font-medium hover:bg-secondary transition-colors"
              data-testid="error-home-btn"
            >
              Torna alla dashboard
            </button>
          </div>
          <details className="text-left">
            <summary className="text-xs text-muted-foreground cursor-pointer select-none">Dettagli tecnici</summary>
            <pre className="mt-2 text-[11px] text-red-700 bg-red-50 border border-red-100 rounded-sm p-3 overflow-auto max-h-48 whitespace-pre-wrap" data-testid="error-detail">{full}</pre>
          </details>
        </div>
      </div>
    );
  }
}

export function ErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundaryInner routeKey={location.pathname}>{children}</ErrorBoundaryInner>;
}
